import React, { useMemo } from 'react';
import styled from 'styled-components';
import { media } from '../../theme/tokens';
import { DataTable } from '../../components/DataTable';
import { NegoceFilterBar } from './NegoceFilterBar';
import { useNegoceFilters } from './NegoceFilters';
import { useTopNegociants, useTopCustomers } from 'apiTyped/hooks/negoce.query';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatNumber = (n: number | undefined | null): string =>
  n != null ? n.toLocaleString('fr-FR') : '—';

// ─── Component ──────────────────────────────────────────────────────────────

export const NegoceRankings: React.FC = () => {
  const { campaignId, wineId, saleTypeId } = useNegoceFilters();

  const { isLoadingTopNegociants, topNegociants } = useTopNegociants({
    campaignId,
    saleTypeId,
    wineId
  });

  const { isLoadingTopCustomers, topCustomers } = useTopCustomers({
    campaignId,
    saleTypeId,
    wineId
  });

  const negociantsData = (topNegociants as any)?.data || [];
  const customersData = (topCustomers as any)?.data || [];

  const negociantColumns = useMemo(
    () => [
      {
        key: 'rank',
        header: '#',
        width: '5%',
        render: (_: any, i: number) => <Rank>{i + 1}</Rank>
      },
      {
        key: 'name',
        header: 'Négociant',
        sortable: true,
        width: '30%',
        render: (row: any) => <strong>{row.name}</strong>
      },
      {
        key: 'bottles_sold',
        header: 'Bottles sold',
        sortable: true,
        width: '18%',
        render: (row: any) => <Mono>{formatNumber(row.bottles_sold)}</Mono>
      },
      {
        key: 'customers_count',
        header: 'Customers',
        sortable: true,
        width: '14%',
        render: (row: any) => <Mono>{formatNumber(row.customers_count)}</Mono>
      },
      {
        key: 'exclusivity_rate',
        header: 'Exclusivity',
        sortable: true,
        width: '14%',
        render: (row: any) => (
          <RateCell $good={(row.exclusivity_rate || 0) >= 50}>
            {row.exclusivity_rate != null
              ? `${row.exclusivity_rate.toFixed(1)}%`
              : '—'}
          </RateCell>
        )
      },
      {
        key: 'loyalty_rate',
        header: 'Loyalty',
        sortable: true,
        width: '14%',
        render: (row: any) => (
          <RateCell $good={(row.loyalty_rate || 0) >= 50}>
            {row.loyalty_rate != null ? `${row.loyalty_rate.toFixed(1)}%` : '—'}
          </RateCell>
        )
      }
    ],
    []
  );

  const customerColumns = useMemo(
    () => [
      {
        key: 'rank',
        header: '#',
        width: '5%',
        render: (_: any, i: number) => <Rank>{i + 1}</Rank>
      },
      {
        key: 'name',
        header: 'Customer',
        sortable: true,
        width: '30%',
        render: (row: any) => <strong>{row.name}</strong>
      },
      {
        key: 'country',
        header: 'Country',
        width: '18%',
        render: (row: any) => <span>{row.country_name || row.country}</span>
      },
      {
        key: 'bottles_sold',
        header: 'Bottles sold',
        sortable: true,
        width: '18%',
        render: (row: any) => <Mono>{formatNumber(row.bottles_sold)}</Mono>
      },
      {
        key: 'negociant',
        header: 'Négociant',
        width: '20%',
        render: (row: any) => <span>{row.negociant_name}</span>
      }
    ],
    []
  );

  return (
    <Page>
      <Header>
        <Title>Rankings</Title>
        <Subtitle>
          Top 20 négociants and customers. Identify your best-performing
          partners and track their key metrics.
        </Subtitle>
      </Header>

      <NegoceFilterBar />

      {/* Top Négociants */}
      <TableSection>
        <TableHeader>
          <CardTitle>Top 20 Négociants</CardTitle>
          <CardMeta>
            Ranked by bottles sold · {negociantsData.length} entries
          </CardMeta>
        </TableHeader>
        <DataTable
          columns={negociantColumns}
          data={negociantsData.slice(0, 20)}
          unavailable={!campaignId && !isLoadingTopNegociants}
          emptyMessage={
            isLoadingTopNegociants ? 'Loading...' : 'No data for this selection'
          }
        />
      </TableSection>

      {/* Top Customers */}
      <TableSection>
        <TableHeader>
          <CardTitle>Top 20 Customers</CardTitle>
          <CardMeta>
            Ranked by bottles sold · {customersData.length} entries
          </CardMeta>
        </TableHeader>
        <DataTable
          columns={customerColumns}
          data={customersData.slice(0, 20)}
          unavailable={!campaignId && !isLoadingTopCustomers}
          emptyMessage={
            isLoadingTopCustomers ? 'Loading...' : 'No data for this selection'
          }
        />
      </TableSection>
    </Page>
  );
};

// ─── Styled ─────────────────────────────────────────────────────────────────

const Page = styled.div`
  max-width: 1400px;
`;

const Header = styled.header`
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 500;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: var(--ink-muted);
  margin: 0;
  max-width: 640px;
  line-height: 1.5;
`;

const TableSection = styled.section`
  margin-bottom: 28px;
`;

const TableHeader = styled.div`
  margin-bottom: 12px;
`;

const CardTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 4px;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
`;

const Rank = styled.span`
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--ink-faint);
`;

const Mono = styled.span`
  font-variant-numeric: tabular-nums;
`;

const RateCell = styled.span<{ $good: boolean }>`
  font-weight: 500;
  color: ${({ $good }) => ($good ? 'var(--olive)' : 'var(--ink-muted)')};
`;
